"use client";

// V2 Step 10e — soft banner for non-Chromium / mobile returning users in
// the V2 migration state. Per V2 spec § Returning user migration paths
// row 3: "soft banner pointing to Chromium desktop. Don't force a modal."
// Persistent until user clicks "Got it" → silent migration write
// (onboarding_completed_v2=true) → banner unmounts naturally via profile
// refresh changing the BannerSlot registry condition.
//
// Companion to V2MigrationModal (10d) which handles the Chromium hard-block
// path. Mutually exclusive at the registry condition level: 10e checks
// !isFileSystemAccessSupported(), 10d checks isFileSystemAccessSupported().

import { useState } from "react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { emitSignal } from "../lib/signal";

export default function V2CloudOnlyBanner() {
  const { user, fetchProfile } = useAuth();
  const [busy, setBusy] = useState(false);

  const handleAcknowledge = async () => {
    if (!user?.id || busy) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed_v2: true })
        .eq("id", user.id);
      if (error) {
        emitSignal("vault_returning_user_cloud_only_failed", "warning", { error: error.message });
        setBusy(false);
        return;
      }
      emitSignal("vault_returning_user_cloud_only_acknowledged", "info", {});
      // Profile refresh triggers BannerSlot re-evaluation → registry
      // condition fails → banner unmounts naturally.
      await fetchProfile(user.id);
    } catch (err) {
      emitSignal("vault_returning_user_cloud_only_failed", "warning", { error: err?.message });
      setBusy(false);
    }
  };

  return (
    <div
      role="complementary"
      aria-label="Your library is in the cloud — Chrome desktop adds a mirror on your computer"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-light)",
        borderRadius: "var(--radius-md)",
        marginBottom: "var(--space-4)",
        fontFamily: "var(--font-primary)",
      }}
    >
      <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text)", lineHeight: 1.5 }}>
        Welcome back. Your library is in the cloud. Chrome desktop adds a mirror on your computer.
      </div>
      <button
        onClick={handleAcknowledge}
        disabled={busy}
        style={{
          padding: "var(--space-2) var(--space-4)",
          background: "var(--color-accent)",
          color: "var(--color-text-inverse)",
          border: "none",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          fontWeight: "var(--font-weight-semibold)",
          fontFamily: "var(--font-primary)",
          cursor: busy ? "not-allowed" : "pointer",
          flexShrink: 0,
        }}
      >
        {busy ? "…" : "Got it"}
      </button>
    </div>
  );
}
